import axios from 'axios';
import { STAFF_TOKEN, MESSAGES_PER_PAGE } from '../../constants';

// Remove the moment import

const API_URL = process.env.REACT_APP_API_URL;

// Create an axios instance with default config
const api = axios.create({
  baseURL: API_URL,
});

// Create a separate instance for public endpoints
const publicApi = axios.create({
  baseURL: API_URL,
});

// Add a request interceptor to include the token in all requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem(STAFF_TOKEN);
  if (token) {
    config.headers.Authorization = `Token ${token}`;
  }
  // Log request details for debugging
  if (config.url && config.url.includes('/messages/')) {
    console.log(`[staffApi interceptor] URL: ${config.url}, params:`, config.params);
  }
  return config;
}, (error) => {
  return Promise.reject(error);
});

export const staffApi = {
  login: async (credentials) => {
    try {
      const response = await publicApi.post('/api/login/', credentials);
      return response.data.data;
    } catch (error) {
      console.error('Login error:', error.response?.data || error.message);
      throw error;
    }
  },

  getStaffBasicInfo: async () => {
    try {
      const response = await api.get('/api/staff/profile/');
      return response.data.data;
    } catch (error) {
      console.error('Error fetching staff info:', error.response?.data || error.message);
      throw error;
    }
  },

  sendMessage: async (chatroomId, content) => {
    try {
      const response = await api.post(`/api/chats/${chatroomId}/send_message/`, {
        content: content
      });
      return response.data;
    } catch (error) {
      console.error('Error sending message:', error.response?.data || error.message);
      throw error;
    }
  },

  markMessagesAsRead: async (chatroomId) => {
    try {
      const response = await api.post(`/api/chats/${chatroomId}/mark_as_read/`);
      return response.data;
    } catch (error) {
      console.error('Error marking messages as read:', error.response?.data || error.message);
      throw error;
    }
  },

  getUnreadMessages: async (chatroomId) => {
    try {
      const response = await api.get(`/api/chats/${chatroomId}/unread_messages/`);
      return response.data;
    } catch (error) {
      console.error('Error fetching unread messages:', error.response?.data || error.message);
      throw error;
    }
  },

  getChatrooms: async() => {
    try {
        const response = await api.get('/api/chats');
        return response.data;
      } catch (error) {
        console.error('Error fetching chatrooms:', error);
        throw error;
      }
  },

  getChatroomMessages: async (chatroomId, limit = MESSAGES_PER_PAGE, offset = 0) => {
    try {
      console.log(`[staffApi] getChatroomMessages - chatroomId=${chatroomId}, limit=${limit}, offset=${offset}`);
      
      // Construct URL with query parameters manually to ensure they're included
      const url = `/api/chats/${chatroomId}/messages/?limit=${limit}&offset=${offset}`;
      console.log(`[staffApi] Request URL: ${url}`);
      
      const response = await api.get(url);
      console.log(`[staffApi] Final request URL: ${response.config.url}`);
      console.log(`[staffApi] Response received - status: ${response.status}, data keys:`, Object.keys(response.data || {}));
      return response.data;
    } catch (error) {
      console.error('Error fetching messages:', error.response?.data || error.message);
      throw error;
    }
  },

  createChatRoom: async (chatRoomData) => {
    try {
      const response = await api.post('/api/chats/', chatRoomData);
      return response.data;
    } catch (error) {
      console.error('Error creating chat room:', error.response?.data || error.message);
      throw error;
    }
  },

  markMessagesAsRead: async (chatroomId) => {
    try {
      const response = await api.post(`/api/chats/${chatroomId}/mark_as_read/`);
      return response.data;
    } catch (error) {
      console.error('Error marking messages as read:', error.response?.data || error.message);
      throw error;
    }
  },

  // Students Management API functions
  getStudents: async (params = {}) => {
    try {
      // Build query parameters
      const queryParams = new URLSearchParams();
      if (params.search) {
        queryParams.append('search', params.search);
      }

      const response = await api.get(`/api/staff/students/?${queryParams.toString()}`);
      
      // The backend now returns data in { students: [...], total: number } format
      // Transform it to match the expected frontend format
      if (response.data.success && response.data.data.students) {
        return {
          ...response.data,
          data: response.data.data.students,
          total: response.data.data.total
        };
      }
      return response.data;
    } catch (error) {
      console.error('Error fetching students:', error.response?.data || error.message);
      throw error;
    }
  },

  getStudentById: async (studentId) => {
    try {
      const response = await api.get(`/api/staff/students/${studentId}/`);
      return response.data;
    } catch (error) {
      console.error('Error fetching student details:', error.response?.data || error.message);
      throw error;
    }
  },

  createStudent: async (studentData) => {
    try {
      const response = await api.post('/api/staff/students/create/', studentData);
      return response.data;
    } catch (error) {
      console.error('Error creating student:', error.response?.data || error.message);
      throw error;
    }
  },

  updateStudent: async (studentId, studentData) => {
    try {
      // Send data in flat structure as expected by the updated backend
      const response = await api.put(`/api/staff/students/${studentId}/update/`, studentData);
      return response.data;
    } catch (error) {
      console.error('Error updating student:', error.response?.data || error.message);
      throw error;
    }
  },



  sendEmailToStudent: async (studentId, subject, message) => {
    try {
      const response = await api.post(`/api/staff/students/${studentId}/send-email/`, {
        subject,
        message
      });
      return response.data;
    } catch (error) {
      console.error('Error sending email:', error.response?.data || error.message);
      throw error;
    }
  },

  sendWhatsAppToStudent: async (studentId, message) => {
    try {
      const response = await api.post(`/api/staff/students/${studentId}/send-whatsapp/`, {
        message
      });
      return response.data;
    } catch (error) {
      console.error('Error sending WhatsApp message:', error.response?.data || error.message);
      throw error;
    }
  },

  createMagicLinkForStudent: async (studentId) => {
    try {
      const response = await api.post(`/api/staff/students/${studentId}/create-magic-link/`);
      return response.data;
    } catch (error) {
      console.error('Error creating magic link for student:', error.response?.data || error.message);
      throw error;
    }
  },

  createLessonPack: async (lessonPackData) => {
    try {
      const response = await api.post('/api/staff/lessonpacks/create/', lessonPackData);
      return response.data;
    } catch (error) {
      console.error('Error creating lesson pack:', error.response?.data || error.message);
      throw error;
    }
  },

  getBookings: async (page = 1, pageSize = 50) => {
    try {
      const response = await api.get(`/api/staff/bookings/?page=${page}&page_size=${pageSize}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching bookings:', error.response?.data || error.message);
      throw error;
    }
  },

  getRecordings: async () => {
    try {
      const response = await api.get('/api/staff/recordings/');
      if (response.data.success) {
        return response.data.data;
      }
      throw new Error(response.data.message || 'Failed to fetch recordings');
    } catch (error) {
      console.error('Error fetching recordings:', error.response?.data || error.message);
      throw error;
    }
  },

  getRecordingDownloadUrl: async (bookingId, s3Key) => {
    try {
      const response = await api.post(`/api/recordings/${bookingId}/download-url/`, { s3_key: s3Key });
      if (response.data.success) {
        return response.data.data.download_url;
      }
      throw new Error(response.data.message || 'Failed to get download URL');
    } catch (error) {
      console.error('Error getting download URL:', error.response?.data || error.message);
      throw error;
    }
  },

  // Tutors Management API functions
  getTutors: async (params = {}) => {
    try {
      const queryParams = new URLSearchParams();
      if (params.search) queryParams.append('search', params.search);
      if (params.subject) queryParams.append('subject', params.subject);
      
      const url = `/api/staff/tutors/${queryParams.toString() ? '?' + queryParams.toString() : ''}`;
      const response = await api.get(url);
      return response.data;
    } catch (error) {
      console.error('Error fetching tutors:', error.response?.data || error.message);
      throw error;
    }
  },

  getTutorById: async (tutorId) => {
    try {
      const response = await api.get(`/api/staff/tutors/${tutorId}/`);
      return response.data;
    } catch (error) {
      console.error('Error fetching tutor details:', error.response?.data || error.message);
      throw error;
    }
  },

  createTutor: async (tutorData) => {
    try {
      const response = await api.post('/api/staff/tutors/create/', tutorData);
      return response.data;
    } catch (error) {
      console.error('Error creating tutor:', error.response?.data || error.message);
      throw error;
    }
  },

  getSubjects: async () => {
    try {
      const response = await api.get('/api/staff/subjects/');
      return response.data;
    } catch (error) {
      console.error('Error fetching subjects:', error.response?.data || error.message);
      throw error;
    }
  },

  sendEmailToTutor: async (tutorId, subject, message) => {
    try {
      const response = await api.post(`/api/staff/tutors/${tutorId}/send-email/`, {
        subject,
        message
      });
      return response.data;
    } catch (error) {
      console.error('Error sending email to tutor:', error.response?.data || error.message);
      throw error;
    }
  },

  sendWhatsAppToTutor: async (tutorId, message) => {
    try {
      const response = await api.post(`/api/staff/tutors/${tutorId}/send-whatsapp/`, {
        message
      });
      return response.data;
    } catch (error) {
      console.error('Error sending WhatsApp message to tutor:', error.response?.data || error.message);
      throw error;
    }
  },

  createMagicLinkForTutor: async (tutorId) => {
    try {
      const response = await api.post(`/api/staff/tutors/${tutorId}/create-magic-link/`);
      return response.data;
    } catch (error) {
      console.error('Error creating magic link for tutor:', error.response?.data || error.message);
      throw error;
    }
  },

  // Tutor Schedule Management API functions
  getTutorSchedule: async (tutorId, year, month) => {
    try {
      const response = await api.get('/api/staff/tutors/schedule/', {
        params: { tutor_id: tutorId, year, month }
      });
      return response.data.data;
    } catch (error) {
      console.error('Error fetching tutor schedule:', error.response?.data || error.message);
      throw error;
    }
  },

  getTutorsForSchedule: async () => {
    try {
      const response = await api.get('/api/staff/tutors/');
      return response.data.data;
    } catch (error) {
      console.error('Error fetching tutors list:', error.response?.data || error.message);
      throw error;
    }
  },

  // Staff Schedule Management CRUD Operations
  updateTutorSchedule: async (tutorId, scheduleData) => {
    try {
      const response = await api.post('/api/staff/tutors/schedule/', {
        tutor_id: tutorId,
        ...scheduleData
      });
      return response.data.data;
    } catch (error) {
      console.error('Error updating tutor schedule:', error.response?.data || error.message);
      throw error;
    }
  },

  deleteTutorSchedule: async (tutorId, date) => {
    try {
      const response = await api.delete('/api/staff/tutors/schedule/', {
        params: { tutor_id: tutorId, date }
      });
      return response.data;
    } catch (error) {
      console.error('Error deleting tutor schedule:', error.response?.data || error.message);
      throw error;
    }
  },

  copyLastWeekSchedule: async (tutorId) => {
    try {
      const response = await api.post('/api/staff/tutors/schedule/bulk/', {
        tutor_id: tutorId
      });
      return response.data;
    } catch (error) {
      console.error('Error copying last week schedule:', error.response?.data || error.message);
      throw error;
    }
  },



  getTutorById: async (tutorId) => {
    try {
      const response = await api.get(`/api/staff/tutors/${tutorId}/`);
      return response.data;
    } catch (error) {
      console.error('Error fetching tutor details:', error.response?.data || error.message);
      throw error;
    }
  },

  getSubjects: async () => {
    try {
      const response = await api.get('/api/staff/subjects/');
      return response.data;
    } catch (error) {
      console.error('Error fetching subjects:', error.response?.data || error.message);
      throw error;
    }
  },

  sendEmailToTutor: async (tutorId, subject, message) => {
    try {
      const response = await api.post(`/api/staff/tutors/${tutorId}/send-email/`, {
        subject,
        message
      });
      return response.data;
    } catch (error) {
      console.error('Error sending email to tutor:', error.response?.data || error.message);
      throw error;
    }
  },

  sendWhatsAppToTutor: async (tutorId, message) => {
    try {
      const response = await api.post(`/api/staff/tutors/${tutorId}/send-whatsapp/`, {
        message
      });
      return response.data;
    } catch (error) {
      console.error('Error sending WhatsApp message to tutor:', error.response?.data || error.message);
      throw error;
    }
  },

  // Lesson Packs Management API functions
  getLessonPacks: async (params = {}) => {
    try {
      const queryParams = new URLSearchParams();
      if (params.search) queryParams.append('search', params.search);
      if (params.status) queryParams.append('status', params.status);
      
      const url = `/api/staff/lessonpacks/${queryParams.toString() ? '?' + queryParams.toString() : ''}`;
      const response = await api.get(url);
      return response.data;
    } catch (error) {
      console.error('Error fetching lesson packs:', error.response?.data || error.message);
      throw error;
    }
  },

  // Mock data for backward compatibility - remove when all components are updated
  getLessonPacksMock: async (params = {}) => {
    try {
      // Mock data - replace with actual API call later
      const mockLessonPacks = [
        {
          id: 1,
          student: {
            id: 1,
            name: 'John Smith',
            email: 'john.smith@email.com',
            phone_no: '+1-555-0123'
          },
          tutor: {
            id: 1,
            name: 'Dr. Sarah Johnson',
            email: 'sarah.johnson@mathsforce.com'
          },
          no_of_hours: 20,
          package_size: '20',
          sessions_remaining: 15,
          start_date: '2024-01-15',
          end_date: '2024-04-15',
          subject: 'Mathematics',
          currency: 'USD',
          price: 500.00,
          status: 'ACTIVE',
          hours_remaining: 15.0,
          purchase_date: '2024-01-15',
          expiry_date: '2024-04-15',
          notes: 'Algebra fundamentals package',
          meeting_link: 'https://meet.google.com/abc-defg-hij'
        },
        {
          id: 2,
          student: {
            id: 2,
            name: 'Emily Davis',
            email: 'emily.davis@email.com',
            phone_no: '+1-555-0124'
          },
          tutor: {
            id: 2,
            name: 'Prof. Michael Brown',
            email: 'michael.brown@mathsforce.com'
          },
          no_of_hours: 10,
          package_size: '10',
          sessions_remaining: 3,
          start_date: '2024-01-10',
          end_date: '2024-04-10',
          subject: 'Chemistry',
          currency: 'USD',
          price: 250.00,
          status: 'ACTIVE',
          hours_remaining: 3.0,
          purchase_date: '2024-01-10',
          expiry_date: '2024-04-10',
          notes: 'Organic chemistry basics',
          meeting_link: 'https://meet.google.com/xyz-uvw-rst'
        },
        {
          id: 3,
          student: {
            id: 3,
            name: 'Alex Wilson',
            email: 'alex.wilson@email.com',
            phone_no: '+1-555-0125'
          },
          tutor: {
            id: 3,
            name: 'Dr. Lisa Chen',
            email: 'lisa.chen@mathsforce.com'
          },
          no_of_hours: 5,
          package_size: '5',
          sessions_remaining: 0,
          start_date: '2024-01-05',
          end_date: '2024-02-05',
          subject: 'Mathematics',
          currency: 'USD',
          price: 125.00,
          status: 'COMPLETED',
          hours_remaining: 0.0,
          purchase_date: '2024-01-05',
          expiry_date: '2024-02-05',
          notes: 'Basic arithmetic review',
          meeting_link: 'https://meet.google.com/mno-pqr-stu'
        },
        {
          id: 4,
          student: {
            id: 4,
            name: 'Sophia Martinez',
            email: 'sophia.martinez@email.com',
            phone_no: '+1-555-0126'
          },
          tutor: {
            id: 4,
            name: 'Prof. David Wilson',
            email: 'david.wilson@mathsforce.com'
          },
          no_of_hours: 25,
          package_size: '25',
          sessions_remaining: 20,
          start_date: '2024-01-12',
          end_date: '2024-05-12',
          subject: 'Physics',
          currency: 'USD',
          price: 625.00,
          status: 'ACTIVE',
          hours_remaining: 20.0,
          purchase_date: '2024-01-12',
          expiry_date: '2024-05-12',
          notes: 'Advanced physics concepts',
          meeting_link: 'https://meet.google.com/vwx-yza-bcd'
        },
        {
          id: 5,
          student: {
            id: 5,
            name: 'David Lee',
            email: 'david.lee@email.com',
            phone_no: '+1-555-0127'
          },
          tutor: {
            id: 5,
            name: 'Dr. Maria Garcia',
            email: 'maria.garcia@mathsforce.com'
          },
          no_of_hours: 15,
          package_size: '15',
          sessions_remaining: 1,
          start_date: '2024-01-18',
          end_date: '2024-03-18',
          subject: 'Computer Science',
          currency: 'CAD',
          price: 375.00,
          status: 'ACTIVE',
          hours_remaining: 1.0,
          purchase_date: '2024-01-18',
          expiry_date: '2024-03-18',
          notes: 'Data structures and algorithms',
          meeting_link: 'https://meet.google.com/efg-hij-klm'
        },
        {
          id: 6,
          student: {
            id: 6,
            name: 'Emma Thompson',
            email: 'emma.thompson@email.com',
            phone_no: '+1-555-0128'
          },
          tutor: {
            id: 1,
            name: 'Dr. Sarah Johnson',
            email: 'sarah.johnson@mathsforce.com'
          },
          no_of_hours: 20,
          package_size: '20',
          sessions_remaining: 0,
          start_date: '2023-12-01',
          end_date: '2024-01-01',
          subject: 'Mathematics',
          currency: 'USD',
          price: 500.00,
          status: 'CANCELED',
          hours_remaining: 0.0,
          purchase_date: '2023-12-01',
          expiry_date: '2024-01-01',
          notes: 'Calculus preparation - expired',
          meeting_link: 'https://meet.google.com/nop-qrs-tuv'
        }
      ];

      // Simulate API delay
      await new Promise(resolve => setTimeout(resolve, 500));

      // Apply search filter if provided
      let filteredLessonPacks = mockLessonPacks;
      if (params.search) {
        const searchTerm = params.search.toLowerCase();
        filteredLessonPacks = mockLessonPacks.filter(pack => {
          return pack.student.name.toLowerCase().includes(searchTerm) ||
                 pack.tutor.name.toLowerCase().includes(searchTerm) ||
                 pack.subject.toLowerCase().includes(searchTerm) ||
                 pack.student.email.toLowerCase().includes(searchTerm);
        });
      }

      // Apply status filter if provided
      if (params.status) {
        filteredLessonPacks = filteredLessonPacks.filter(pack => pack.status === params.status);
      }

      return {
        success: true,
        data: filteredLessonPacks,
        total: filteredLessonPacks.length
      };
    } catch (error) {
      console.error('Error fetching lesson packs:', error);
      throw error;
    }
  },

  getLessonPackById: async (packId) => {
    try {
      const response = await api.get(`/api/staff/lessonpacks/${packId}/`);
      return response.data;
    } catch (error) {
      console.error('Error fetching lesson pack details:', error.response?.data || error.message);
      throw error;
    }
  },

  // Mock data for backward compatibility - remove when all components are updated
  getLessonPackByIdMock: async (packId) => {
    try {
      // Mock data - replace with actual API call later
      const mockLessonPack = {
        id: parseInt(packId),
        student: {
          id: 1,
          name: 'John Smith',
          email: 'john.smith@email.com',
          phone_no: '+1-555-0123',
          timezone: 'America/New_York'
        },
        tutor: {
          id: 1,
          name: 'Dr. Sarah Johnson',
          email: 'sarah.johnson@mathsforce.com',
          phone_no: '+1-555-0200'
        },
        no_of_hours: 20,
        package_size: '20',
        sessions_remaining: 15,
        start_date: '2024-01-15',
        end_date: '2024-04-15',
        subject: 'Mathematics',
        currency: 'USD',
        price: 500.00,
        status: 'ACTIVE',
        hours_remaining: 15.0,
        purchase_date: '2024-01-15',
        expiry_date: '2024-04-15',
        notes: 'Algebra fundamentals package - student shows good progress',
        meeting_link: 'https://meet.google.com/abc-defg-hij',
        sessions: [
          {
            id: 1,
            date: '2024-01-20',
            duration: 60,
            subject: 'Mathematics',
            tutor_name: 'Dr. Sarah Johnson',
            notes: 'Quadratic equations - student showed good understanding of factoring methods',
            status: 'COMPLETED'
          },
          {
            id: 2,
            date: '2024-01-18',
            duration: 45,
            subject: 'Mathematics',
            tutor_name: 'Dr. Sarah Johnson',
            notes: 'Linear equations - excellent progress with word problems',
            status: 'COMPLETED'
          },
          {
            id: 3,
            date: '2024-01-15',
            duration: 50,
            subject: 'Mathematics',
            tutor_name: 'Dr. Sarah Johnson',
            notes: 'Introduction to algebra - student was engaged and asked good questions',
            status: 'COMPLETED'
          },
          {
            id: 4,
            date: '2024-01-25',
            duration: 0,
            subject: 'Mathematics',
            tutor_name: 'Dr. Sarah Johnson',
            notes: 'Scheduled for polynomial functions',
            status: 'SCHEDULED'
          }
        ]
      };

      // Simulate API delay
      await new Promise(resolve => setTimeout(resolve, 300));

      return {
        success: true,
        data: mockLessonPack
      };
    } catch (error) {
      console.error('Error fetching lesson pack details:', error);
      throw error;
    }
  },

  addSession: async (packId, sessionData) => {
    try {
      // Mock data - replace with actual API call later
      const newSession = {
        id: Date.now(),
        ...sessionData,
        date: new Date().toISOString().split('T')[0],
        status: 'COMPLETED'
      };

      // Simulate API delay
      await new Promise(resolve => setTimeout(resolve, 600));

      return {
        success: true,
        data: newSession,
        message: 'Session added successfully'
      };
    } catch (error) {
      console.error('Error adding session:', error);
      throw error;
    }
  },

  updateLessonPack: async (packId, packData) => {
    try {
      const response = await api.put(`/api/staff/lessonpacks/${packId}/`, packData);
      return response.data;
    } catch (error) {
      console.error('Error updating lesson pack:', error.response?.data || error.message);
      throw error;
    }
  },


  // Payment Management API functions
  getTutorPayments: async () => {
    try {
      const response = await api.get('/api/staff/payments/tutor-info/');
      return response.data;
    } catch (error) {
      console.error('Error fetching tutor payments:', error.response?.data || error.message);
      throw error;
    }
  },

  getPaymentRecords: async () => {
    try {
      const response = await api.get('/api/staff/payments/records/');
      return response.data;
    } catch (error) {
      console.error('Error fetching payment records:', error.response?.data || error.message);
      throw error;
    }
  },


  markPaymentAsPaid: async (recordId) => {
    try {
      const response = await api.post(`/api/staff/payments/records/${recordId}/mark-paid/`);
      return response.data;
    } catch (error) {
      console.error('Error marking payment as paid:', error.response?.data || error.message);
      throw error;
    }
  },

  processPayout: async (tutorId) => {
    try {
      const response = await api.post(`/api/staff/payments/process-payout/${tutorId}/`);
      return response.data;
    } catch (error) {
      console.error('Error processing payout:', error.response?.data || error.message);
      throw error;
    }
  },

  // Messaging API functions
  getMessagingTemplates: async () => {
    try {
      // Mock data for messaging templates
      const mockTemplates = [
        {
          id: 1,
          name: 'Demo Invite',
          description: 'Invite students to book a demo lesson',
          content: 'Hi {{name}}, we\'d love to invite you to a free demo lesson! Book your session at {{booking_link}} to experience our personalized tutoring approach.',
          channel: 'email',
          category: 'demo'
        },
        {
          id: 2,
          name: 'Payment Reminder',
          description: 'Remind students about pending payments',
          content: 'Hi {{name}}, this is a friendly reminder that your payment of ${{amount}} for {{service}} is due on {{due_date}}. Please complete your payment to continue your lessons.',
          channel: 'email',
          category: 'payment'
        },
        {
          id: 3,
          name: 'Lesson Reminder',
          description: 'Remind students about upcoming lessons',
          content: 'Hi {{name}}, just a reminder that you have a {{subject}} lesson scheduled for {{lesson_date}} at {{lesson_time}}. We look forward to seeing you!',
          channel: 'email',
          category: 'lesson'
        },
        {
          id: 4,
          name: 'Demo Invite (WhatsApp)',
          description: 'WhatsApp version of demo invite',
          content: 'Hi {{name}}! 🎓 Ready to boost your learning? Book a FREE demo lesson with us! Click here: {{booking_link}} #Mathsforce #Learning',
          channel: 'whatsapp',
          category: 'demo'
        },
        {
          id: 5,
          name: 'Payment Reminder (WhatsApp)',
          description: 'WhatsApp payment reminder',
          content: 'Hi {{name}}! 💳 Friendly reminder: Your payment of ${{amount}} is due {{due_date}}. Complete it here: {{payment_link}} Thanks! 🙏',
          channel: 'whatsapp',
          category: 'payment'
        },
        {
          id: 6,
          name: 'Lesson Reminder (WhatsApp)',
          description: 'WhatsApp lesson reminder',
          content: 'Hi {{name}}! 📚 Your {{subject}} lesson is tomorrow at {{lesson_time}}. See you soon! 🚀 #Mathsforce',
          channel: 'whatsapp',
          category: 'lesson'
        }
      ];
      
      // Simulate API delay
      await new Promise(resolve => setTimeout(resolve, 500));
      return mockTemplates;
    } catch (error) {
      console.error('Error fetching messaging templates:', error);
      throw error;
    }
  },

  getMessagingStudents: async () => {
    try {
      // Mock data for students (for messaging)
      const mockStudents = [
        {
          id: 1,
          name: 'John Smith',
          email: 'john.smith@email.com',
          phone: '+1234567890',
          grade: '10th',
          subjects: ['Math', 'Physics']
        },
        {
          id: 2,
          name: 'Sarah Johnson',
          email: 'sarah.johnson@email.com',
          phone: '+1234567891',
          grade: '9th',
          subjects: ['Math', 'Chemistry']
        },
        {
          id: 3,
          name: 'Mike Davis',
          email: 'mike.davis@email.com',
          phone: '+1234567892',
          grade: '11th',
          subjects: ['Math', 'Physics', 'Chemistry']
        },
        {
          id: 4,
          name: 'Emily Wilson',
          email: 'emily.wilson@email.com',
          phone: '+1234567893',
          grade: '8th',
          subjects: ['Math']
        },
        {
          id: 5,
          name: 'Alex Brown',
          email: 'alex.brown@email.com',
          phone: '+1234567894',
          grade: '12th',
          subjects: ['Math', 'Physics', 'Chemistry', 'Biology']
        }
      ];
      
      // Simulate API delay
      await new Promise(resolve => setTimeout(resolve, 300));
      return mockStudents;
    } catch (error) {
      console.error('Error fetching students:', error);
      throw error;
    }
  },

  getMessagingTutors: async () => {
    try {
      // Mock data for tutors (for messaging)
      const mockTutors = [
        {
          id: 1,
          name: 'Dr. Robert Chen',
          email: 'robert.chen@mathsforce.com',
          phone: '+1234567800',
          subjects: ['Math', 'Physics'],
          experience: '8 years',
          rating: 4.9
        },
        {
          id: 2,
          name: 'Prof. Lisa Martinez',
          email: 'lisa.martinez@mathsforce.com',
          phone: '+1234567801',
          subjects: ['Math', 'Chemistry'],
          experience: '12 years',
          rating: 4.8
        },
        {
          id: 3,
          name: 'Dr. James Wilson',
          email: 'james.wilson@mathsforce.com',
          phone: '+1234567802',
          subjects: ['Math', 'Physics', 'Chemistry'],
          experience: '15 years',
          rating: 4.9
        },
        {
          id: 4,
          name: 'Ms. Anna Thompson',
          email: 'anna.thompson@mathsforce.com',
          phone: '+1234567803',
          subjects: ['Math', 'Biology'],
          experience: '6 years',
          rating: 4.7
        },
        {
          id: 5,
          name: 'Dr. Michael Lee',
          email: 'michael.lee@mathsforce.com',
          phone: '+1234567804',
          subjects: ['Math', 'Physics', 'Chemistry', 'Biology'],
          experience: '10 years',
          rating: 4.8
        }
      ];
      
      // Simulate API delay
      await new Promise(resolve => setTimeout(resolve, 300));
      return mockTutors;
    } catch (error) {
      console.error('Error fetching tutors:', error);
      throw error;
    }
  },

  sendTemplateMessage: async (data) => {
    try {
      const { templateId, recipientId, recipientType, channel } = data;
      
      // Mock API call - simulate sending
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      // Log the action (in real implementation, this would make actual API calls)
      console.log('Template message sent:', {
        templateId,
        recipientId,
        recipientType,
        channel,
        timestamp: new Date().toISOString()
      });
      
      return {
        success: true,
        messageId: `msg_${Date.now()}`,
        status: 'sent'
      };
    } catch (error) {
      console.error('Error sending template message:', error);
      throw error;
    }
  },

  sendDirectMessage: async (data) => {
    try {
      const { recipientId, recipientType, channel, message } = data;
      
      // Mock API call - simulate sending
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      // Log the action (in real implementation, this would make actual API calls)
      console.log('Direct message sent:', {
        recipientId,
        recipientType,
        channel,
        message: message.substring(0, 50) + '...',
        timestamp: new Date().toISOString()
      });
      
      return {
        success: true,
        messageId: `msg_${Date.now()}`,
        status: 'sent'
      };
    } catch (error) {
      console.error('Error sending direct message:', error);
      throw error;
    }
  },

  // Future integration functions (for when real APIs are implemented)
  sendEmailViaSendGrid: async (data) => {
    try {
      // This would integrate with SendGrid API
      console.log('SendGrid email integration:', data);
      return { success: true, messageId: `email_${Date.now()}` };
    } catch (error) {
      console.error('SendGrid error:', error);
      throw error;
    }
  },

  sendWhatsAppViaTwilio: async (data) => {
    try {
      // This would integrate with Twilio WhatsApp API
      console.log('Twilio WhatsApp integration:', data);
      return { success: true, messageId: `whatsapp_${Date.now()}` };
    } catch (error) {
      console.error('Twilio WhatsApp error:', error);
      throw error;
    }
  },

  // Chat-related API methods
  getChatrooms: async () => {
    try {
      const response = await api.get('/api/chats/');
      return response.data;
    } catch (error) {
      console.error('Error fetching chatrooms:', error.response?.data || error.message);
      throw error;
    }
  },

  sendMessage: async (chatroomId, content) => {
    try {
      const response = await api.post(`/api/chats/${chatroomId}/send_message/`, {
        content: content
      });
      return response.data;
    } catch (error) {
      console.error('Error sending message:', error.response?.data || error.message);
      throw error;
    }
  },

  createChatRoom: async (chatRoomData) => {
    try {
      const response = await api.post('/api/chats/', chatRoomData);
      return response.data;
    } catch (error) {
      console.error('Error creating chat room:', error.response?.data || error.message);
      throw error;
    }
  },

  // Demo File Sharing API functions
  createDemoFileShare: async (demoId) => {
    try {
      const response = await api.post(`/api/staff/demos/${demoId}/file-share/`);
      return response.data;
    } catch (error) {
      console.error('Error creating demo file share:', error.response?.data || error.message);
      throw error;
    }
  },

  getDemoFileShare: async (demoId) => {
    try {
      const response = await api.get(`/api/staff/demos/${demoId}/file-share/detail/`);
      return response.data;
    } catch (error) {
      console.error('Error fetching demo file share:', error.response?.data || error.message);
      throw error;
    }
  },

  deactivateDemoFileShare: async (demoId) => {
    try {
      const response = await api.delete(`/api/staff/demos/${demoId}/file-share/detail/`);
      return response.data;
    } catch (error) {
      console.error('Error deactivating demo file share:', error.response?.data || error.message);
      throw error;
    }
  },

  // General File Sharing API functions
  getFileShares: async (page = 1, pageSize = 10) => {
    try {
      const response = await api.get(`/api/staff/file-shares/?page=${page}&page_size=${pageSize}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching file shares:', error.response?.data || error.message);
      throw error;
    }
  },

  createFileShare: async (data) => {
    try {
      const response = await api.post('/api/staff/file-shares/create/', data);
      return response.data;
    } catch (error) {
      console.error('Error creating file share:', error.response?.data || error.message);
      throw error;
    }
  },

  getFileShare: async (shareId) => {
    try {
      const response = await api.get(`/api/staff/file-shares/${shareId}/`);
      return response.data;
    } catch (error) {
      console.error('Error fetching file share:', error.response?.data || error.message);
      throw error;
    }
  },

  deactivateFileShare: async (shareId) => {
    try {
      const response = await api.delete(`/api/staff/file-shares/${shareId}/`);
      return response.data;
    } catch (error) {
      console.error('Error deactivating file share:', error.response?.data || error.message);
      throw error;
    }
  },

  // Tutor Interview Test APIs
  getTutorTests: async () => {
    try {
      const response = await api.get('/api/staff/tests/');
      return response.data.data;
    } catch (error) {
      console.error('Error fetching tutor tests:', error.response?.data || error.message);
      throw error;
    }
  },

  createTutorTest: async (testData) => {
    try {
      const response = await api.post('/api/staff/tests/create/', testData);
      return response.data.data;
    } catch (error) {
      console.error('Error creating tutor test:', error.response?.data || error.message);
      throw error;
    }
  },

  getTutorTest: async (testId) => {
    try {
      const response = await api.get(`/api/staff/tests/${testId}/`);
      return response.data.data;
    } catch (error) {
      console.error('Error fetching tutor test:', error.response?.data || error.message);
      throw error;
    }
  },

  deleteTutorTest: async (testId) => {
    try {
      const response = await api.delete(`/api/staff/tests/${testId}/`);
      return response.data;
    } catch (error) {
      console.error('Error deleting tutor test:', error.response?.data || error.message);
      throw error;
    }
  },

  sendTestLink: async (testId, tutorEmail, tutorName = '') => {
    try {
      const response = await api.post(`/api/staff/tests/${testId}/send-link/`, {
        tutor_email: tutorEmail,
        tutor_name: tutorName
      });
      return response.data;
    } catch (error) {
      console.error('Error sending test link:', error.response?.data || error.message);
      throw error;
    }
  },

  createTestLink: async (testId, tutorEmail, tutorName = '') => {
    try {
      const response = await api.post(`/api/staff/tests/${testId}/links/create/`, {
        tutor_email: tutorEmail,
        tutor_name: tutorName
      });
      return response.data.data;
    } catch (error) {
      console.error('Error creating test link:', error.response?.data || error.message);
      throw error;
    }
  },

  getTestLinks: async (testId) => {
    try {
      const response = await api.get(`/api/staff/tests/${testId}/links/`);
      return response.data.data;
    } catch (error) {
      console.error('Error fetching test links:', error.response?.data || error.message);
      throw error;
    }
  },

  sendTestLinkEmail: async (linkId) => {
    try {
      const response = await api.post(`/api/staff/test-links/${linkId}/send-email/`);
      return response.data;
    } catch (error) {
      console.error('Error sending test link email:', error.response?.data || error.message);
      throw error;
    }
  },

  getTestSubmissions: async (testId) => {
    try {
      const response = await api.get(`/api/staff/tests/${testId}/submissions/`);
      return response.data.data;
    } catch (error) {
      console.error('Error fetching test submissions:', error.response?.data || error.message);
      throw error;
    }
  },

  getSubmissionDetail: async (submissionId) => {
    try {
      const response = await api.get(`/api/staff/submissions/${submissionId}/`);
      return response.data.data;
    } catch (error) {
      console.error('Error fetching submission detail:', error.response?.data || error.message);
      throw error;
    }
  }

};

export default staffApi;